import React, { useState, useEffect } from "react";
import { X } from 'lucide-react';
import { Button } from "../components/ui/button";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isPast } from 'date-fns';
import { ref, onValue } from '../lib/firebase-database';
import { db } from "../firebase"; 



const CheckAvailabilityModal = ({ isOpen, onClose, villaId, villaName, onChatWithOwner }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedDates, setBookedDates] = useState([]);
 
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fetch booked dates from Firebase
  useEffect(() => {
    if (!villaId) return;

    const bookingsRef = ref(db, `bookings/${villaId}`);
    const unsubscribe = onValue(bookingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const bookingsData = snapshot.val();
        const dates = [];
        Object.values(bookingsData).forEach((booking) => {
          if (booking.startDate && booking.endDate) {
            const start = new Date(booking.startDate);
            const end = new Date(booking.endDate);
            let currentDate = new Date(start);
            while (currentDate <= end) {
              dates.push(new Date(currentDate));
              currentDate.setDate(currentDate.getDate() + 1);
            }
          }
        });
        setBookedDates(dates);
      } else {
        setBookedDates([]);
      }
    });

    return () => unsubscribe();
  }, [villaId]);

  if (!isOpen) return null;

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const isDateBooked = (date) => {
    return bookedDates.some(d => isSameDay(d, date));
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleChatWithOwner = () => {
    if (onChatWithOwner) {
      onChatWithOwner();
    }
    onClose();
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 relative z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Check Availability</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">{villaName}</h3>
          <p className="text-gray-600">View availability calendar below.</p>
        </div>

        {/* Custom Calendar */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-gray-800">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={previousMonth}
                  className="px-3 py-1 bg-gray-300 hover:bg-gray-200 text-gray-900 rounded-md text-sm transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={nextMonth}
                  className="px-3 py-1 bg-gray-300 hover:bg-gray-200 text-gray-900 rounded-md text-sm transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-0.5 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: monthStart.getDay() }).map((_, index) => (
                <div key={`empty-${index}`} className="h-10 sm:h-12" />
              ))}

              {monthDays.map((date, index) => {
                const isBooked = isDateBooked(date);
                const isPastDate = isPast(date) && !isToday(date);

                return (
                  <div
                    key={index}
                    className={`
                      h-10 sm:h-12 rounded-md flex items-center justify-center text-xs sm:text-sm
                      transition-colors relative cursor-default
                      ${isBooked ? 'bg-orange-200 text-orange-800' : 'bg-white text-gray-800'}
                      ${isPastDate ? 'opacity-50' : ''}
                    `}
                  >
                    {format(date, 'd')}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-orange-200 rounded"></div>
              <span>Unavailable</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gray-200 rounded"></div>
              <span>Available</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckAvailabilityModal;